
import React, { useState, useMemo } from "react";
import { Users, TrendingUp, Youtube, Instagram, Trophy, Medal, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAllInfluencers } from "@/hooks/useAllInfluencers";
import { useCampaigns } from "@/hooks/useCampaigns";


const platforms = [
  { label: "All", value: "" },
  { label: "Instagram", value: "Instagram" },
  { label: "YouTube", value: "YouTube" }
];

const timeRanges = [
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
  { label: "Last year", value: "1y" }
];

const sortOptions = [
  { label: "Performance Score", value: "score" },
  { label: "Engagement Rate", value: "engagement" },
  { label: "Average ROI", value: "roi" },
  { label: "Campaign Count", value: "campaigns" }
];

// Performance scoring algorithm
const calculatePerformanceScore = (influencer: any) => {
  const engagementWeight = 0.4;
  const roiWeight = 0.4;
  const campaignWeight = 0.2;

  const engagement = Number(influencer.engagement) || 0;
  const rawRoi = influencer.avgROI ? String(influencer.avgROI).replace(/[^0-9.-]/g, "") : "0";
  const roiParsed = parseFloat(rawRoi);
  const roi = isNaN(roiParsed) ? 0 : roiParsed;
  const campaigns = Number(influencer.campaigns) || 0;

  const score = engagement * engagementWeight + roi * roiWeight + campaigns * campaignWeight;
  return isNaN(score) ? 0 : score;
};

const getInstagramUrl = (handle?: string) => {
  if (!handle) return '#';
  const trimmed = handle.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  const cleanHandle = trimmed.replace(/^@/, '');
  return `https://www.instagram.com/${cleanHandle}/`;
};

// Get rank styling for top 3
const getRankStyling = (rank) => {
  switch (rank) {
    case 1:
      return {
        icon: Trophy,
        className: "text-yellow-500",
        bgClass: "bg-yellow-50"
      };
    case 2:
      return {
        icon: Medal,
        className: "text-gray-400",
        bgClass: "bg-gray-50"
      };
    case 3:
      return {
        icon: Award,
        className: "text-amber-600",
        bgClass: "bg-amber-50"
      };
    default:
      return null;
  }
};

const TopPerformingInfluencers: React.FC = () => {
  const [platformFilter, setPlatformFilter] = useState("");
  const [timeRange, setTimeRange] = useState("30d");
  const [sortBy, setSortBy] = useState("score");

  // Supabase data
  const { data: allCampaigns = [] } = useCampaigns();
  const { data: allInfluencers = [], isLoading, error } = useAllInfluencers();

  // Filter and sort influencers
  const processedInfluencers = useMemo(() => {
    // If brand has no campaigns, there are no campaign participants
    if (!allCampaigns || allCampaigns.length === 0) {
      return [];
    }

    // Only include influencers who have participated in campaigns
    const campaignInfluencers = allInfluencers.filter((i) => (Number(i.campaigns) || 0) > 0);

    // Filter by platform
    const filteredInfluencers = platformFilter === ""
      ? campaignInfluencers
      : campaignInfluencers.filter((i) => i.platform === platformFilter);

    // Calculate performance scores
    const influencersWithScores = filteredInfluencers.map(influencer => ({
      ...influencer,
      performanceScore: calculatePerformanceScore(influencer)
    }));

    // Sort based on selected criteria
    const sortedInfluencers = [...influencersWithScores].sort((a, b) => {
      switch (sortBy) {
        case "engagement":
          return (b.engagement || 0) - (a.engagement || 0);
        case "roi":
          const roiA = parseFloat((a.avgROI || "0x").replace("x", ""));
          const roiB = parseFloat((b.avgROI || "0x").replace("x", ""));
          return roiB - roiA;
        case "campaigns":
          return (b.campaigns || 0) - (a.campaigns || 0);
        case "score":
        default:
          return b.performanceScore - a.performanceScore;
      }
    });

    return sortedInfluencers;
  }, [allInfluencers, platformFilter, sortBy]);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Top Performing Influencers</h1>
          <p className="text-gray-600 text-sm mt-1">
            Ranked by performance score based on engagement, ROI, and campaign success
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={platformFilter}
            onChange={e => setPlatformFilter(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            {platforms.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <select
            value={timeRange}
            onChange={e => setTimeRange(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            {timeRanges.map(tr => (
              <option key={tr.value} value={tr.value}>{tr.label}</option>
            ))}
          </select>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <Card>
        <CardContent>
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-8 text-center text-gray-600">Loading influencers...</div>
            ) : error ? (
              <div className="p-8 text-center text-red-600">Failed to load influencers.</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Rank</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Influencer</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-900">Platform</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Engagement Rate</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Reach</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Campaigns</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Avg. Fee</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Avg. ROI</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {processedInfluencers.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-6 text-center text-gray-500">
                        No top performing influencers found.
                      </td>
                    </tr>
                  ) : processedInfluencers.map((influencer, idx) => {
                    const rank = idx + 1;
                    const rankStyling = getRankStyling(rank);
                    const RankIcon = rankStyling?.icon;

                    const rawPhotoCandidate =
                      influencer.profilePicUrl ||
                      influencer.avatar ||
                      (Array.isArray(influencer.thumbnails) && typeof influencer.thumbnails[0] === 'string' ? influencer.thumbnails[0] : null);

                    const photoUrl =
                      typeof rawPhotoCandidate === 'string' &&
                      (rawPhotoCandidate.startsWith('http') || rawPhotoCandidate.startsWith('/') || rawPhotoCandidate.startsWith('data:'))
                        ? rawPhotoCandidate
                        : null;

                    return (
                      <tr
                        key={influencer.id}
                        className={`border-b border-gray-100 hover:bg-gray-50 ${rankStyling?.bgClass || ''}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            {RankIcon ? (
                              <RankIcon className={`w-5 h-5 ${rankStyling.className}`} />
                            ) : (
                              <span className="text-sm font-medium text-gray-600">#{rank}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center overflow-hidden shrink-0 relative">
                            {photoUrl ? (
                              <img
                                src={photoUrl}
                                alt={influencer.name}
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                                  const sibling = e.currentTarget.nextElementSibling as HTMLElement | null;
                                  if (sibling) sibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <span
                              className="text-white text-xs font-semibold items-center justify-center w-full h-full"
                              style={{
                                display: photoUrl ? 'none' : 'flex',
                              }}
                            >
                              {influencer.name
                                ? influencer.name
                                  .split(' ')
                                  .map((n: string) => n[0])
                                  .join('')
                                  .slice(0, 2)
                                  .toUpperCase()
                                : 'IN'}
                            </span>
                          </div>
                          <div className="items-center">
                            <div className="font-medium text-gray-900">{influencer.name}</div>
                            {influencer.handle ? (
                              <a
                                href={getInstagramUrl(influencer.handle)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-gray-500 hover:text-[#A655F8] hover:underline cursor-pointer inline-block"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {influencer.handle}
                              </a>
                            ) : (
                              <div className="text-xs text-gray-500">-</div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex justify-center items-center">
                            {influencer.platform === "YouTube" ? (
                              <span title="YouTube"><Youtube className="w-5 h-5 text-red-500" /></span>
                            ) : (
                              <img
                                src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png"
                                alt="Instagram"
                                title="Instagram"
                                className="w-5 h-5 object-contain"
                              />
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">{influencer.engagement ?? "-"}%</td>
                        <td className="py-3 px-4">{influencer.reach ?? "-"}</td>
                        <td className="py-3 px-4">{influencer.campaigns ?? "-"}</td>
                        <td className="py-3 px-4">{influencer.avgFee ?? "-"}</td>
                        <td className="py-3 px-4">{influencer.avgROI ?? "-"}</td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {influencer.performanceScore?.toFixed(1) || "0.0"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TopPerformingInfluencers;
